// components/booking/BookingDateStep.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

interface BookingDateStepProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

export const BookingDateStep: React.FC<BookingDateStepProps> = ({
  selectedDate,
  onDateSelect,
  setError,
  setLoading
}) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const calendarInstance = useRef<Calendar | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Helper function to get today at midnight (production-safe)
  const getTodayAtMidnight = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Helper function to get today as ISO date string
  const getTodayDateString = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!calendarRef.current) return;

    const todayAtMidnight = getTodayAtMidnight();

    // Initialize FullCalendar
    const calendar = new Calendar(calendarRef.current, {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      locale: 'ar',
      direction: 'rtl',
      firstDay: 0, // Sunday
      headerToolbar: {
        left: 'prev,next',
        center: 'title',
        right: 'today'
      },
      buttonText: {
        today: 'اليوم',
        prev: 'السابق',
        next: 'التالي'
      },
      height: 'auto',
      selectable: !isMobile, // Only enable drag selection on desktop
      selectMirror: !isMobile,
      unselectAuto: true,
      // Fixed: Use today at midnight to allow same-day booking
      validRange: {
        start: todayAtMidnight
      },
      selectConstraint: {
        start: todayAtMidnight
      },
      // Handle date selection for desktop (drag-select)
      select: (selectInfo) => {
        if (!isMobile) {
          const selectedDate = new Date(selectInfo.start);
          const todayMidnight = getTodayAtMidnight();
          
          // Validate that selected date is today or future
          if (selectedDate >= todayMidnight) {
            onDateSelect(selectInfo.startStr);
          }
        }
      },
      // Handle date click for mobile (single tap)
      dateClick: (info) => {
        // Only use dateClick on mobile devices
        if (isMobile) {
          const clickedDate = new Date(info.date);
          const todayMidnight = getTodayAtMidnight();
          
          // Only allow selection of today or future dates
          if (clickedDate >= todayMidnight) {
            onDateSelect(info.dateStr);
          }
        }
      },
      dayCellContent: (arg) => {
        const date = arg.date;
        const todayMidnight = getTodayAtMidnight();
        const isPast = date < todayMidnight;
        
        return {
          html: `
            <div class="fc-daygrid-day-number ${isPast ? 'text-gray-400' : 'text-gray-900'} p-2">
              ${arg.dayNumberText}
            </div>
          `
        };
      },
      dayCellDidMount: (arg) => {
        const date = arg.date;
        const todayMidnight = getTodayAtMidnight();
        const isPast = date < todayMidnight;
        
        if (isPast) {
          arg.el.classList.add('fc-day-disabled');
          arg.el.style.backgroundColor = '#f9fafb';
          arg.el.style.color = '#9ca3af';
          arg.el.style.cursor = 'not-allowed';
        } else {
          arg.el.style.cursor = 'pointer';
          
          // Add touch-friendly styles for mobile
          if (isMobile) {
            arg.el.style.WebkitTapHighlightColor = 'rgba(59, 130, 246, 0.2)';
            arg.el.style.touchAction = 'manipulation';
          }
          
          // Desktop hover effects
          arg.el.addEventListener('mouseenter', () => {
            if (!isPast && !arg.el.classList.contains('fc-day-selected')) {
              arg.el.style.backgroundColor = '#eff6ff';
            }
          });
          arg.el.addEventListener('mouseleave', () => {
            if (!isPast && !arg.el.classList.contains('fc-day-selected')) {
              arg.el.style.backgroundColor = '';
            }
          });
        }
        
        // Highlight selected date
        if (selectedDate && arg.date.toISOString().split('T')[0] === selectedDate) {
          arg.el.classList.add('fc-day-selected');
          arg.el.style.backgroundColor = '#3b82f6';
          arg.el.style.color = 'white';
        }
      }
    });

    calendar.render();
    calendarInstance.current = calendar;

    return () => {
      if (calendarInstance.current) {
        calendarInstance.current.destroy();
      }
    };
  }, [onDateSelect, selectedDate, isMobile]);

  const formatSelectedDate = (dateStr: string) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return date.toLocaleDateString('ar-JO', options);
  };

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">اختر تاريخ الجلسة</h2>
        <p className="text-gray-600">اختر التاريخ المناسب لجلستك التعليمية</p>
      </div>

      {/* Instructions based on device */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center">
          <div className="text-blue-600 ml-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-blue-800 text-sm">
            {isMobile 
              ? 'اضغط على التاريخ لاختياره' 
              : 'انقر واسحب لتحديد التاريخ أو انقر على يوم واحد'}
          </p>
        </div>
      </div>

      {/* Selected date display */}
      {selectedDate && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-green-600 ml-3">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-green-800 font-medium">التاريخ المحدد:</p>
              <p className="text-green-700">{formatSelectedDate(selectedDate)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Calendar */}
      <div className="mb-6">
        <div 
          ref={calendarRef} 
          className="booking-calendar"
          style={{
            '--fc-border-color': '#e5e7eb',
            '--fc-today-bg-color': '#dbeafe',
            '--fc-neutral-bg-color': '#f9fafb'
          } as React.CSSProperties}
        />
      </div>

      {/* Instructions */}
      <div className="text-center text-sm text-gray-500">
        <p>يمكنك حجز جلسة من اليوم فصاعداً</p>
        <p className="mt-1">اختر التاريخ المناسب لك وسنعرض عليك الأوقات المتاحة</p>
      </div>

      <style jsx>{`
        .booking-calendar :global(.fc-day-disabled) {
          pointer-events: none !important;
        }
        
        .booking-calendar :global(.fc-day-selected),
        .booking-calendar :global(.fc-day-selected:hover) {
          background-color: #3b82f6 !important;
          color: white !important;
        }
        
        .booking-calendar :global(.fc-day-selected .fc-daygrid-day-number) {
          color: white !important;
        }
        
        .booking-calendar :global(.fc-highlight) {
          background-color: #dbeafe !important;
          opacity: 1 !important;
        }
        
        .booking-calendar :global(.fc-button) {
          background-color: #3b82f6;
          border-color: #3b82f6;
          color: white;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }
        
        .booking-calendar :global(.fc-button:hover) {
          background-color: #2563eb;
          border-color: #2563eb;
        }
        
        .booking-calendar :global(.fc-button:disabled) {
          background-color: #9ca3af;
          border-color: #9ca3af;
        }
        
        .booking-calendar :global(.fc-toolbar-title) {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }
        
        .booking-calendar :global(.fc-col-header-cell) {
          background-color: #f9fafb;
          border-color: #e5e7eb;
          font-weight: 600;
          color: #374151;
        }
        
        .booking-calendar :global(.fc-daygrid-day) {
          border-color: #e5e7eb;
          min-height: 2.5rem;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        
        .booking-calendar :global(.fc-daygrid-day:hover:not(.fc-day-disabled)) {
          background-color: #eff6ff;
        }
        
        /* Fixed: Better styling for today - blue instead of yellow */
        .booking-calendar :global(.fc-today) {
          background-color: #dbeafe !important;
          border: 2px solid #3b82f6 !important;
        }
        
        .booking-calendar :global(.fc-today:hover:not(.fc-day-disabled)) {
          background-color: #bfdbfe !important;
        }
        
        /* Ensure today can be selected when it's the selected date */
        .booking-calendar :global(.fc-today.fc-day-selected) {
          background-color: #3b82f6 !important;
          color: white !important;
          border: 2px solid #1e40af !important;
        }
        
        /* Mobile-specific improvements */
        @media (max-width: 768px) {
          .booking-calendar :global(.fc-toolbar) {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .booking-calendar :global(.fc-toolbar-chunk) {
            display: flex;
            justify-content: center;
          }
          
          .booking-calendar :global(.fc-button) {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
            min-height: 44px; /* iOS touch target size */
            min-width: 44px;
          }
          
          .booking-calendar :global(.fc-toolbar-title) {
            font-size: 1.125rem;
            margin: 0.5rem 0;
          }
          
          .booking-calendar :global(.fc-daygrid-day) {
            min-height: 44px; /* iOS touch target size */
          }
          
          .booking-calendar :global(.fc-daygrid-day-number) {
            font-size: 0.875rem;
            padding: 0.5rem;
          }
          
          /* Active state for touch feedback */
          .booking-calendar :global(.fc-daygrid-day:active:not(.fc-day-disabled)) {
            background-color: #dbeafe !important;
            transform: scale(0.98);
          }
          
          .booking-calendar :global(.fc-button:active) {
            transform: scale(0.95);
          }
        }
      `}</style>
    </div>
  );
};